import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";
import styles from "./Box.module.css";

/**
 * Box — layout primitive.
 *
 * Thin flexbox wrapper that maps a short vocabulary of spacing / alignment
 * props onto design tokens. Default render is `<div>` with `display: flex`;
 * pass `inline` to switch to `inline-flex`, or `as` to render a different
 * element (e.g. `as="section"`).
 *
 * Spacing tokens use a 5-step scale (xs=4px, sm=8px, md=12px, lg=16px,
 * xl=24px) — these map to the Figma `--spacing-{4,8,12,16,24}` tokens.
 * Other spacing values (2/20/28 px) are available via raw CSS; Box only
 * exposes the commonly-used subset.
 */

type Spacing = "xs" | "sm" | "md" | "lg" | "xl";
type Direction = "row" | "column";
type Align = "start" | "center" | "end" | "stretch" | "baseline";
type Justify = "start" | "center" | "end" | "between";

/**
 * NOTE on `as` polymorphism: this is a deliberate simplification.
 *
 * `BoxProps` extends the attribute set of `<div>` — so `<Box as="button">`
 * compiles but does NOT type-check `disabled`, `type`, etc. as button
 * attributes. Same for `<Box as="a" href="…">`: `href` is not in the
 * allowed props. Full generic polymorphism (e.g. `BoxProps<T extends ElementType>`)
 * would give correct narrowing but adds conditional-type complexity
 * we don't need at this scale.
 *
 * If you need element-specific attributes (href on a link, type/disabled
 * on a button), use the native element directly or widen the prop type
 * at the call site with a cast. If this pattern repeats, consider
 * promoting BoxProps to a generic.
 */
export type BoxProps = {
  /** Element to render. Defaults to `div`. */
  as?: ElementType;
  /** Symmetric padding on all four sides. */
  padding?: Spacing;
  /** Horizontal padding (overrides horizontal axis of `padding`). */
  paddingX?: Spacing;
  /** Vertical padding (overrides vertical axis of `padding`). */
  paddingY?: Spacing;
  /** Gap between flex children. */
  gap?: Spacing;
  /** Flex direction. Defaults to `row`. */
  direction?: Direction;
  /** `align-items`. */
  align?: Align;
  /** `justify-content`. */
  justify?: Justify;
  /** Render as `inline-flex` instead of `flex`. */
  inline?: boolean;
  /** Extra class name(s) to merge with the generated Box classes. */
  className?: string;
  children?: ReactNode;
} & Omit<ComponentPropsWithoutRef<"div">, "className" | "children">;

export function Box({
  as,
  padding,
  paddingX,
  paddingY,
  gap,
  direction,
  align,
  justify,
  inline,
  className,
  children,
  ...rest
}: BoxProps) {
  const Component = (as ?? "div") as ElementType;
  const classes = [
    styles.box,
    inline ? styles.inline : null,
    padding ? styles[`p-${padding}`] : null,
    paddingX ? styles[`px-${paddingX}`] : null,
    paddingY ? styles[`py-${paddingY}`] : null,
    gap ? styles[`gap-${gap}`] : null,
    direction ? styles[`dir-${direction}`] : null,
    align ? styles[`align-${align}`] : null,
    justify ? styles[`justify-${justify}`] : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <Component className={classes} {...rest}>
      {children}
    </Component>
  );
}
